'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { useCustomerDashboard } from '@/hooks/useCustomerDashboard';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { fadeInVariants } from '@/lib/animations';

interface CustomerPanelHeaderProps {
  language?: string;
  translations?: Record<string, string>;
}

export default function CustomerPanelHeader({
  language = 'en',
  translations = {},
}: CustomerPanelHeaderProps) {
  const { customer } = useCustomerAuth();
  const t = useCustomerPanelTranslations(translations);
  const { data: dashboardData } = useCustomerDashboard();

  // Customer bilgisi
  const customerName = customer 
    ? `${customer.first_name} ${customer.last_name}`.trim()
    : '';

  // Yaklaşan rezervasyonlar (confirmed ve pending rezervasyonlar)
  const upcomingReservations = dashboardData?.reservations_summary
    ? (dashboardData.reservations_summary.confirmed + dashboardData.reservations_summary.pending)
    : 0;
  const openDues = dashboardData?.open_payment_dues_count ?? 0;

  if (!customerName) {
    return null;
  }

  return (
    <motion.div
      className="bg-gradient-to-r from-alsero-first via-red-600 to-alsero-first rounded-lg shadow-tour-card overflow-hidden mb-6 relative group group-hover:shadow-2xl transition-shadow duration-500"
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full -ml-24 -mb-24"></div>
      </div>
      
      <div className="relative px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Sol taraf - Kullanıcı bilgisi */}
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <motion.div 
                className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-white font-bold text-2xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                {customerName.charAt(0).toUpperCase()}
              </motion.div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {t('customer_panel.header.welcome', 'Welcome')}, {customerName}
              </h2>
              <p className="text-sm text-white/90 mt-1">
                {t('customer_panel.header.manage_account', 'Manage your account and reservations')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-stretch gap-3">
            {upcomingReservations > 0 && (
              <motion.div
                className="flex items-center gap-3 bg-white/20 backdrop-blur-sm px-5 py-4 rounded-xl border border-white/30 shadow-lg"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="p-2 bg-white/30 rounded-lg">
                  <CalendarIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/80 uppercase tracking-wide">
                    {t('customer_panel.header.upcoming_reservations', 'Upcoming Reservations')}
                  </p>
                  <p className="text-2xl font-bold text-white">{upcomingReservations}</p>
                </div>
              </motion.div>
            )}
            {openDues > 0 && (
              <motion.div
                className="flex items-center gap-3 bg-white/20 backdrop-blur-sm px-5 py-4 rounded-xl border border-white/30 shadow-lg"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <div className="p-2 bg-white/30 rounded-lg">
                  <CalendarIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/80 uppercase tracking-wide">
                    {t('customer_panel.header.open_dues', 'Open installments')}
                  </p>
                  <p className="text-2xl font-bold text-white">{openDues}</p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

